import type { ApproveWorkPeriodNode } from '@/modules/salary/types.graphql'
import { format } from '@/utils/date-fns'
import { parseISO } from 'date-fns'
import { useMemo } from 'react'
import type { CommentDayGroup, CommentEntry, Role } from './types'

interface Params {
  workPeriods: Pick<
    ApproveWorkPeriodNode,
    | 'id'
    | 'user'
    | 'leaderGroup'
    | 'datetimeStart'
    | 'datetimeEnd'
    | 'userComment'
    | 'managerComment'
    | 'userApprover'
    | 'managerApprover'
  >[]
  roleFilter: Role | null
}

// Transforms work periods into a flat list of comment entries, one for each user (driver) and manager (leader) comment
export const buildCommentEntries = (
  workPeriods: Pick<
    ApproveWorkPeriodNode,
    | 'id'
    | 'user'
    | 'leaderGroup'
    | 'datetimeStart'
    | 'datetimeEnd'
    | 'userComment'
    | 'managerComment'
    | 'userApprover'
    | 'managerApprover'
  >[]
): CommentEntry[] => {
  const result: CommentEntry[] = []
  for (const wp of workPeriods) {
    const start = parseISO(wp.datetimeStart)
    const end = parseISO(wp.datetimeEnd)
    const dateKey = format(start, 'yyyy-MM-dd')
    const dayLabel = format(start, 'EEEE, MMMM d, yyyy')
    const timeRange = `${format(start, 'HH:mm')} → ${format(end, 'HH:mm')}`
    const base = {
      dateKey,
      dayLabel,
      timeRange,
      sortKey: start.getTime(),
      userId: wp.user.id,
      userName: wp.user.fullName,
    }
    if (wp.userComment && wp.userComment.trim()) {
      result.push({
        ...base,
        id: `${wp.id}-driver`,
        role: 'driver',
        text: wp.userComment,
        approverName: wp.userApprover?.fullName ?? null,
      })
    }
    if (wp.managerComment && wp.managerComment.trim()) {
      result.push({
        ...base,
        id: `${wp.id}-leder`,
        role: 'leder',
        text: wp.managerComment,
        approverName: wp.managerApprover?.fullName ?? null,
      })
    }
  }
  return result.sort((a, b) => a.sortKey - b.sortKey)
}

// Filters comment entries by role if a role filter is set
export const filterCommentEntries = (
  entries: CommentEntry[],
  roleFilter: Role | null
): CommentEntry[] => {
  if (!roleFilter) return entries
  return entries.filter(e => e.role === roleFilter)
}

// Groups consecutive entries that share dateKey (and userId, when multi-user) into day groups.
// When the source list contains comments from multiple users, the user name is attached to each
// group so a manager scanning the list can tell whose work day a comment belongs to without
// implying that user authored the comment.
export const groupCommentsByDay = (
  entries: CommentEntry[]
): CommentDayGroup[] => {
  const distinctUsers = new Set(entries.map(e => e.userId))
  const isMultiUser = distinctUsers.size > 1

  const groups: CommentDayGroup[] = []
  for (const entry of entries) {
    const key = isMultiUser ? `${entry.dateKey}-${entry.userId}` : entry.dateKey
    const last = groups[groups.length - 1]
    if (last && last.key === key) {
      last.items.push(entry)
    } else {
      groups.push({
        key,
        dateKey: entry.dateKey,
        dayLabel: entry.dayLabel,
        userName: isMultiUser ? entry.userName : null,
        items: [entry],
      })
    }
  }
  return groups
}

// Custom hook to get comment entries grouped by day, with optional role filtering
export const useApprovalCommentEntries = ({
  workPeriods,
  roleFilter,
}: Params): CommentDayGroup[] => {
  const entries = useMemo(() => buildCommentEntries(workPeriods), [workPeriods])
  const filtered = useMemo(
    () => filterCommentEntries(entries, roleFilter),
    [entries, roleFilter]
  )
  return useMemo(() => groupCommentsByDay(filtered), [filtered])
}
